import React, { useCallback, useMemo } from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  body: {
    padding: 32,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  section: {
    marginBottom: 10,
    padding: 16,
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '50%',
    padding: 5,
  },
  header: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  subHeader: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 12,
    color: '#4B5563',
    marginBottom: 4,
  },
  table: {
    width: '100%',
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 5,
    borderBottom: '1px solid #FDBA74',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 5,
    borderBottom: '1px solid #FDBA74',
  },
  tableCell: {
    flex: 1,
    textAlign: 'center',
    fontSize: 10,
  },
  link: {
    color: 'blue',
    textDecoration: 'underline',
  },
});

const DetailPoolPdf = ({ data, log_history, list_maturity }) => {
  const _parseDate = useCallback((date) => {
    try {
      const res = date.split("T")[0];
      if (res === "0001-01-01") {
        return "-";
      }
      return res;
    } catch (error) {
      return "-";
    }
  }, []);

  const _capitalize = useCallback((str) => {
    if (!str) return "";
    var splitStr = str.toLowerCase().replaceAll("_", " ").split(" ");
    for (var i = 0; i < splitStr.length; i++) {
      splitStr[i] =
        splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    return splitStr.join(" ");
  }, []);

  const _status = useMemo(() => {
    if (!data || !data.status) return "";
    return _capitalize(data.status);
  }, [data, _capitalize]);

  return (
    <Document>
      <Page style={styles.body}>
        <View style={styles.container}>
          {/* Pool Detail Section */}
          <View style={styles.section}>
            <View style={styles.grid}>
              <View style={styles.column}>
                <Text style={styles.header}>Pool Detail</Text>
                <View>
                  <Text style={styles.paragraph}>Pool ID:</Text>
                  <Text style={styles.subHeader}>{data.id ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Pool Name:</Text>
                  <Text style={styles.subHeader}>{data.pool_name ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>OC Level:</Text>
                  <Text style={styles.subHeader}>{data.pool_value ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>OC Percentage:</Text>
                  <Text style={styles.subHeader}>{data.oc_value ? `${data.oc_value}%` : '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Indicative Return:</Text>
                  <Text style={styles.subHeader}>{data.indicative_return}%</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Originator ID:</Text>
                  <Text style={styles.subHeader}>{data.owner_id ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Originator Name:</Text>
                  <Text style={styles.subHeader}>{data.owner_name ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Fund Manager ID:</Text>
                  <Text style={styles.subHeader}>{(data.status === 'new' ? '-' : data.fund_manager_id) ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Fund Manager Name:</Text>
                  <Text style={styles.subHeader}>{(data.status === 'new' ? '-' : data.fund_manager_name) ?? "-"}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Active Start Date:</Text>
                  <Text style={styles.subHeader}>{_parseDate(data.active_date)}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Active End Date:</Text>
                  <Text style={styles.subHeader}>{_parseDate(data.active_until)}</Text>
                </View>
              </View>

              <View style={styles.column}>
                <Text style={styles.header}>Issuer On Board</Text>
                <View>
                  <Text style={styles.paragraph}>Issuer ID:</Text>
                  <Text style={styles.subHeader}>{data.company_id ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Name:</Text>
                  <Text style={styles.subHeader}>{data.company.name ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Email:</Text>
                  <Text style={styles.subHeader}>{data.company.email ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Phone:</Text>
                  <Text style={styles.subHeader}>{data.company.phone ?? '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Rollover Date:</Text>
                  <Text style={styles.subHeader}>{data.roll_over_data?.updated_at ? _parseDate(data.roll_over_data?.updated_at) : '-'}</Text>
                </View>
                <View>
                  <Text style={styles.paragraph}>Rollover Status:</Text>
                  <Text style={styles.subHeader}>{_capitalize(data.roll_over_data?.status) ?? '-'}</Text>
                </View>
              </View>

            </View>
            <View>
              <View>
                <Text style={styles.paragraph}>Status:</Text>
                <Text style={styles.subHeader}>{_status}</Text>
              </View>
              <View style={{ marginTop: 8 }}>
                <Text style={styles.paragraph}>Agreements:</Text>
                {data.pool_agreement &&
                  <Text style={styles.subHeader}>Master Agreement ({data.pool_agreement})</Text>
                }
                {data.transaction_agreement &&
                  <Text style={styles.subHeader}>Transaction Agreement ({data.transaction_agreement})</Text>
                }
                {data.term_sheet &&
                  <Text style={styles.subHeader}>Term Sheet ({data.term_sheet})</Text>
                }
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* List Assets Section */}
      <Page style={styles.body}>
        <View style={styles.section}>
          <Text style={styles.header}>List Assets</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, { fontWeight: 'bold', flex: 2 }]}>SAG No / Financing Ref No</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Disbursement Date</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Maturity Date</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Marhun Value (RM)</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Financing Amount (RM)</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Outstanding Balance (RM)</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold', flex: 0.5 }]}>Status</Text>
            </View>
            {/* Table Rows */}
            {data?.pooling_data?.filter(p => p.status === 'active')?.map(item => (
              <View key={item.reporting_data.financing_no} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 2 }]}>{item.reporting_data.financing_no}</Text>
                <Text style={styles.tableCell}>{_parseDate(item.reporting_data.disbursement_date)}</Text>
                <Text style={styles.tableCell}>{_parseDate(item.reporting_data.maturity_date)}</Text>
                <Text style={styles.tableCell}>{item.reporting_data.marhun_amount}</Text>
                <Text style={styles.tableCell}>{item.reporting_data.financing_amount}</Text>
                <Text style={styles.tableCell}>{item.reporting_data.outstanding_balance}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{_capitalize(item.reporting_data.reporting_status)}</Text>
              </View>
            ))}
            {/* Add other rows similarly */}
          </View>
        </View>
      </Page>

      <Page style={styles.body}>
        {/* Log Replacements Section */}
        <View style={styles.section}>
          <Text style={styles.header}>Log Replacements</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Previous Financing No</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Updated Financing No</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Previous Financing Amount</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Updated Financing No</Text>
            </View>
            {/* Table Rows */}
            {
              log_history?.map((item, index) => (
                <View style={styles.tableRow} key={index}>
                  <Text style={[styles.tableCell]}>{item.prevFinancingNo}</Text>
                  <Text style={[styles.tableCell]}>{item.updatedFinancingNo}</Text>
                  <Text style={[styles.tableCell]}>{item.prevFinancingAmount}</Text>
                  <Text style={[styles.tableCell]}>{item.updatedFinancingAmount}</Text>
                </View>
              ))
            }
          </View>
        </View>
      </Page>

      <Page style={styles.body}>
        {/* Rollover History Section */}
        <View style={styles.section}>
          <Text style={styles.header}>Rollover History</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, { flex: 1.5, fontWeight: 'bold' }]}>ID</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Active Date</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Active Until</Text>
              <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Status</Text>
              <Text style={[styles.tableCell, { flex: 1.5, fontWeight: 'bold' }]}>Agreement Doc</Text>
            </View>
            {
              list_maturity?.map((item, index) => (
                <View style={styles.tableRow} key={index}>
                  <Text style={[styles.tableCell, { flex: 1.5 }]}>{item.id}</Text>
                  <Text style={styles.tableCell}>{_parseDate(item.active_date)}</Text>
                  <Text style={styles.tableCell}>{_parseDate(item.active_until)}</Text>
                  <Text style={styles.tableCell}>{_capitalize(item.status)}</Text>
                  <Text style={[styles.tableCell, { flex: 1.5 }]}>
                    View Document ({item.pool_agreement})
                  </Text>
                </View>
              ))
            }
          </View>
        </View>
      </Page>
    </Document >
  )
};

export default DetailPoolPdf;